import { IconFont, Input, Popover } from '@byecode/ui'
import type { BackgroundType, Border } from '@lighthouse/core'
import { BACKGROUND_TYPE } from '@lighthouse/core'
import { FilledInput, FillPickerPopoverDropdown } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback, useMemo, useState } from 'react'

import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'

interface TextBorderProps {
    // prefixPath?: string
    value: Border | undefined
    onChange: (val: Border) => void
    // onSetBreakKey?: (name: string) => void
    enabledBackgroundTypes?: BackgroundType[]

    customViewDsId?: string
}

const DEFAULT_COLOR_BACKGROUND_TYPES: BackgroundType[] = [BACKGROUND_TYPE.color]

export const TextBorder: React.FC<TextBorderProps> = ({ value, onChange, enabledBackgroundTypes, customViewDsId }) => {
    const colorSystemMethods = useColorSystemAction()

    const variableTypeOption = useGetColorVariableOptions({
        customViewDsId
    })

    const hasBorderValue = useMemo(() => {
        if (!value) {
            return false
        }

        if (value.type === BACKGROUND_TYPE.color) {
            return !!value.color
        }

        if (value.type === BACKGROUND_TYPE.gradient) {
            return !!value.gradient?.stops
        }

        if (value.type === BACKGROUND_TYPE.variable) {
            return !!value.variable
        }
    }, [value])

    const [draftBorderWidth, setDraftBorderWidth] = useState(value?.all?.toString() || '')

    const handleConfirmChangeBorderWidth = useCallback(
        (val: string) => {
            const number = Number(val)
            const min = 0
            const max = 100
            if (Number.isNaN(number) || min > number || number > max) {
                setDraftBorderWidth(value?.all?.toString() || '')
                return
            }

            setDraftBorderWidth(number.toString())
            // setValue(`${textBorderName}.all`, number)
            if (value) {
                onChange({
                    ...value,
                    all: number
                })
            }
        },
        [onChange, value]
    )

    return (
        <>
            <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={168}>
                <Popover.Target>
                    <FilledInput
                        style={{ width: 180 }}
                        value={value}
                        onClear={() => {
                            onChange({ ...value, type: BACKGROUND_TYPE.color, color: '' })
                        }}
                        variableTypeOption={variableTypeOption}
                    />
                </Popover.Target>
                <FillPickerPopoverDropdown
                    title="描边"
                    value={value}
                    onChange={v => {
                        const initBorderWidth = 1
                        setDraftBorderWidth(initBorderWidth.toString())
                        const val = { all: initBorderWidth, ...value, ...v } as Border
                        if (isDeepEqual(val, value)) {
                            return
                        }
                        onChange(val)
                    }}
                    enabledBackgroundTypes={enabledBackgroundTypes || DEFAULT_COLOR_BACKGROUND_TYPES}
                    variableTypeOption={variableTypeOption}
                    {...colorSystemMethods}
                />
            </Popover>
            {hasBorderValue && (
                <Input
                    value={draftBorderWidth}
                    onChange={e => {
                        setDraftBorderWidth(e.target.value)
                    }}
                    onBlur={e => handleConfirmChangeBorderWidth(e.currentTarget.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            handleConfirmChangeBorderWidth(e.currentTarget.value)
                        }
                    }}
                    prefix={<IconFont type="LineSize" />}
                    placeholder=""
                    styles={{ wrapper: { width: 180 } }}
                />
            )}
        </>
    )
}
